import React, { useEffect, useRef, useState } from 'react';
import { Info, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';
import { isAdsPaused, isBoostActive } from '../lib/rewardedVideo';

interface AdSenseBannerProps {
  slot: string;
  format?: 'auto' | 'fluid' | 'rectangle' | 'horizontal';
  className?: string;
  style?: React.CSSProperties;
}

const GAME_TIPS = [
  'Attento al 2 di Denari: catturarlo assegna un malus di -5 punti.',
  'Una Mano Bassa va dichiarata prima di giocare la prima carta.',
  'Il Settebello vale un punto: evita di lasciarlo in una presa facile.',
  'Prendere almeno un Denari evita la penalità Zero Denari da -10 punti.',
  'La Primiera usa la carta migliore di ogni seme: i 7 valgono 21.',
  'Con tre Assi in mano puoi dichiarare Tre Assi e ottenere +6 punti.',
];

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

/**
 * Safe AdSense unit for non-interactive surfaces only.
 * Consent and serving mode are managed by Google Privacy & Messaging/IAB TCF;
 * this component must not infer consent from localStorage.
 */
export default function AdSenseBanner({
  slot,
  format = 'auto',
  className,
  style,
}: AdSenseBannerProps) {
  const [unavailable, setUnavailable] = useState(false);
  const pushedRef = useRef(false);
  const tipIndexRef = useRef(Math.floor(Math.random() * GAME_TIPS.length));

  const disabled = import.meta.env.DEV || isAdsPaused() || isBoostActive();

  useEffect(() => {
    if (disabled || pushedRef.current) return;

    let attempts = 0;
    const pushUnit = () => {
      attempts += 1;
      try {
        if (Array.isArray(window.adsbygoogle)) {
          window.adsbygoogle.push({});
          pushedRef.current = true;
          return true;
        }
      } catch (error) {
        console.warn('[AdSenseBanner] Unable to initialize ad unit:', error);
        setUnavailable(true);
        return true;
      }

      if (attempts >= 20) {
        setUnavailable(true);
        return true;
      }
      return false;
    };

    if (pushUnit()) return;
    const interval = window.setInterval(() => {
      if (pushUnit()) window.clearInterval(interval);
    }, 500);

    return () => window.clearInterval(interval);
  }, [disabled]);

  if (disabled || unavailable) {
    return (
      <aside
        className={cn(
          'w-full min-h-[64px] my-4 rounded-2xl border border-white/5 bg-black/35 px-4 py-3 flex items-center gap-3 text-left',
          className,
        )}
        style={style}
        aria-label="Suggerimento di gioco"
      >
        <div className="w-9 h-9 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center text-yellow-400 shrink-0">
          <Info className="w-4 h-4" aria-hidden="true" />
        </div>
        <div>
          <span className="text-[9px] uppercase tracking-widest text-yellow-400 font-extrabold flex items-center gap-1">
            Sbarazzino Tips <Sparkles className="w-3 h-3" aria-hidden="true" />
          </span>
          <p className="m-0 text-[11px] sm:text-xs text-white/70 leading-normal">
            {GAME_TIPS[tipIndexRef.current]}
          </p>
        </div>
      </aside>
    );
  }

  return (
    <aside
      className={cn(
        'w-full min-h-[90px] my-6 px-3 py-4 overflow-hidden flex justify-center items-center border-y border-white/5',
        className,
      )}
      style={style}
      aria-label="Spazio pubblicitario"
    >
      <ins
        className="adsbygoogle"
        style={{ display: 'block', width: '100%', minWidth: '250px' }}
        data-ad-client={import.meta.env.VITE_ADSENSE_CLIENT_ID || 'ca-pub-3103514852994717'}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </aside>
  );
}
